/**
 * The locked list envelope inner shape (ADR-0006 §5): every `/api` list
 * endpoint replies `{ data: { items, total, page, size } }`, and
 * `http.get` already unwraps the outer `{ data }`, so services return
 * this inner object verbatim.
 *
 * This file is the sole declaration of `ListPage`, and since #2167 PR-2 the
 * only place a `ListPage` is translated: `toListResult` passes its fields
 * through unchanged, with no fallback logic of its own. The zero-value
 * fallback some callers need lives at the call site instead. In
 * `features/admin/pages/webhookFailureResource.tsx`, `listAdapter` pre-fills
 * the object it hands to `toListResult` with `res.page || params.page || 1`
 * and `res.size || DEFAULT_PAGE_SIZE`, using `||` rather than `??` so a page
 * or page size of ZERO takes the fallback just as an absent one would — the
 * case its `WebhookFailureListResponse` (`services/webhook-failures.ts`)
 * leaves open by declaring `page` and `size` as required `number`s.
 *
 * `size` is the LIST KERNEL's reserved key —
 * fastcade's protocol, not a property of a resource controller — so
 * `@resource-kit/controllers` speaks `ListResult` (`limit`, the same word the
 * request side already uses) and the host adapts at its own boundary: this
 * module renames on the way in, `list-request`'s `toApiListParams` renames on
 * the way out.
 *
 * `totalPages` is deliberately absent — the wire never carried it. The list
 * controller derives it for `ListView` from `limit` and `total`.
 */
import type { ListResult } from '@resource-kit/controllers';

export interface ListPage<T> {
  items: T[];
  total: number;
  page: number;
  size: number;
}

/**
 * Adapt a wire page to the controllers' list contract.
 *
 * Written out field by field rather than `{ ...page, limit: page.size }`: a
 * spread would carry `size` across as well, handing the package a member its
 * contract does not declare and letting a reader downstream keep depending on
 * the very key this rename exists to confine. `items` passes by REFERENCE —
 * this renames a key, it does not copy a page of records.
 */
export function toListResult<T>(page: ListPage<T>): ListResult<T> {
  return { items: page.items, total: page.total, page: page.page, limit: page.size };
}
